import { useState } from 'react';
import { Badge, Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { mailErrorMessage, startOutlookConnect, useMailConnection } from '../lib/mail';

/**
 * ProviderPickerModal — "Where should your drafts go?" (spec 0014, PRD decision).
 *
 * The connector is a verification step, not a send screen: each row is a mail provider, and
 * Connect hands the user to that provider's own sign-in. appealstack writes drafts into the
 * connected mailbox and never sends. Microsoft Outlook is the only provider this phase; the
 * rows come from the server's provider list, so a second provider (Gmail) is one more row.
 *
 * `beforeConnect` lets the composer save the half-written draft before the browser leaves.
 */
export interface ProviderPickerModalProps {
  open: boolean;
  onClose: () => void;
  returnTo?: string;
  beforeConnect?: () => Promise<void> | void;
}

export function ProviderPickerModal({ open, onClose, returnTo, beforeConnect }: ProviderPickerModalProps) {
  const { toast } = useToast();
  const q = useMailConnection(open);
  const [busy, setBusy] = useState<string | null>(null);
  const state = q.data;
  const conn = state?.connection ?? null;
  const providers = state?.providers?.length ? state.providers : [{ provider: 'microsoft', label: 'Microsoft Outlook', configured: false }];

  async function connect(provider: string) {
    setBusy(provider);
    try {
      if (beforeConnect) await beforeConnect();
      await startOutlookConnect(returnTo);
    } catch (err) {
      toast({ variant: 'error', message: mailErrorMessage(err) });
      setBusy(null);
    }
  }

  const row = { display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', padding: 'var(--sp-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' } as const;
  const hint = { fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', margin: 0 } as const;

  return (
    <Modal open={open} onClose={onClose} title="Where should your drafts go?" size="sm">
      <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
        <p style={hint}>Link your own mailbox. Email you write here is created as a draft there, and you send it yourself from your mail app. appealstack never sends it.</p>
        <ul aria-label="Mail providers" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--sp-2)' }}>
          {providers.map((p) => {
            const mine = conn && conn.provider === p.provider ? conn : null;
            return (
              <li key={p.provider} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 'var(--fw-medium)' }}>{p.label}</div>
                  <div style={hint}>
                    {!p.configured ? 'Not set up for your firm yet.'
                      : mine?.status === 'connected' ? `Connected as ${mine.account_email ?? 'your mailbox'}`
                        : mine?.status === 'error' ? 'Needs reconnecting.'
                          : 'Not connected.'}
                  </div>
                </div>
                {mine?.status === 'connected' && <Badge variant="success">Connected</Badge>}
                {p.configured && mine?.status !== 'connected' && (
                  <Button variant="primary" size="sm" loading={busy === p.provider} onClick={() => connect(p.provider)}>
                    {mine ? 'Reconnect' : 'Connect'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        <p style={hint}>Manage your link and signatures in <a href="/account/email">My email</a>.</p>
      </div>
    </Modal>
  );
}
